/**
 * The sign-in options the auth server advertises (ADR-018): `GET {issuer}/providers` lists
 * the *enabled* identity providers as `{ id, displayName }` — environments turn providers
 * on by configuration, so the login buttons follow the server rather than a hardcoded list.
 * Failures (and an empty or malformed catalog) fall back to Google, the always-on operator
 * IdP, so sign-in never disappears because a catalog fetch hiccuped.
 */
import { authConfigFor } from './config';

export interface IdentityProviderInfo {
  readonly id: string;
  readonly displayName: string;
}

export const FALLBACK_PROVIDERS: readonly IdentityProviderInfo[] = [
  { id: 'google', displayName: 'Google' },
];

export async function fetchProviders(): Promise<readonly IdentityProviderInfo[]> {
  const { issuer } = authConfigFor(window.location.hostname);
  try {
    const res = await fetch(`${issuer}/providers`);
    if (!res.ok) return FALLBACK_PROVIDERS;
    const body = (await res.json()) as unknown;
    if (!Array.isArray(body)) return FALLBACK_PROVIDERS;
    const providers = body.filter(
      (p): p is IdentityProviderInfo =>
        typeof (p as IdentityProviderInfo).id === 'string' &&
        typeof (p as IdentityProviderInfo).displayName === 'string',
    );
    return providers.length > 0 ? providers : FALLBACK_PROVIDERS;
  } catch {
    return FALLBACK_PROVIDERS;
  }
}
